import { _ } from "../../polyglot/polyglot.js";
import {
	activeCssClass,
	findSelectedItem,
	firstSelectableItem,
	registerSmartOnBlur,
	removeCssClass,
	selectedCssClass,
	selectOption,
	updateButtonLabelFromSelection,
} from "./utils.js";
import { debounce } from "../../helpers/functional-util.js";
import { isInDocument } from "../../helpers/dom-util.js";
import {
	makeEmptyFilterNode,
	makeNode,
	makeSearchableField,
} from "./makeNode.js";
import { registerButtonKeyDown } from "./keyboardNavigation.js";
import { small } from "../size/index.js";
import { thinAngleDownIcon, thinCircleTimesIcon } from "../icons/index.js";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import defineElementWithNoChild from "../helpers/defineElementWithNoChild.js";
import stacker from "../stacker/stacker.js";
import text from "../text/text.js";

function defaultSearchFn({ item, searchText }) {
	return item.label.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
}

/**
 * This element renders a list of options, and provides the user the choice to
 * pick one item out of the list. If the `allowEmptySelection` parameter is
 * provided with `true` then the use can also choose to pick no item at all.
 *
 * Items in the list are referenced by value, which means that:
 *   - all items must have a different value
 *   - all values must be non-null
 *
 * It supports grouping items together by providing grouped items {@link GroupItem}.
 *
 * Technical insights:
 *   - due to the usage of LESS for rendering groups depth, we currently only
 *     support 10 levels of depth (see `@maximum-depth` in `select.less`)
 *   - we decided to not keep a pointer to the selected value, even internally
 *     to avoid future leakage
 *
 * @param {Item[]} items - List of items to render
 * @param {*} [selected] - Value of the selected item. This takes precedence over
 *   item-level selection
 * @param {function(*): void} [onChange] - Function invoked when a new item is selected or
 *   with the first selectable item if no selection is provided and empty
 *   selection is not allowed. The function is invoked with the item value
 * @param {boolean} [allowEmptySelection] - When true, it offers the user the
 *   opportunity to select nothing. It renders a "clear selection" button as
 *   well
 * @param {bool|function} [searchable] - When true, the list can be searched
 *   through.
 *   A function can be provided as well, that will be used for custom search.
 *   In this case, the function will be invoked with an object as following:
 *   { item: {Item}, searchText: string }. See `defaultSearchFn`
 * @param {string} [emptyLabel] - Label to render when empty selection is allowed
 *   and nothing is selected
 * @param {string} [name] - unique identifier for the select element name parameter
 * @param {boolean} [disabled] - True if the select can't be opened or changed
 */
export default defineElementWithNoChild(
	"select",
	({
		items,
		selected = null,
		onChange: baseOnChange,
		allowEmptySelection = false,
		searchable = false,
		emptyLabel = _("Nothing selected"),
		name,
		disabled = false,
	}) => {
		const isOpen = { current: false };
		const selectContainerRef = { current: null };
		let buttonRef = null;
		let buttonLabelRef = null;
		let clearSelectionRef = null;
		const searchFieldRef = { current: null };
		let optionsRef = null;
		let clearSearch = null;
		const itemToNode = new Map();
		const nodeToItem = new Map();
		let callback = null;
		let resolveRendered = null;
		const renderPromise = new Promise((resolve) => {
			resolveRendered = resolve;
		});

		const itemsPromise = Promise.resolve(items);

		function onChange(value) {
			// the select can have been removed from the DOM while computing items.
			// In this case, no need to trigger the onChange
			if (!baseOnChange || !isInDocument(buttonRef)) {
				return;
			}
			baseOnChange(value);
		}

		const onButtonClick = () => {
			isOpen.current = !isOpen.current;

			if (isOpen.current) {
				buttonRef.classList.add("is-open");

				callback = registerSmartOnBlur({
					buttonRef,
					onBlur,
				});
			} else {
				buttonRef.classList.remove("is-open");

				removeCssClass({
					nodeToItem,
					cssClass: activeCssClass,
				});

				buttonRef.focus();

				if (callback) {
					document.body.removeEventListener("click", callback);
				}
			}
		};

		function clearSelection(event) {
			event.preventDefault();
			event.stopPropagation();

			selectOption({
				option: null,
				onChange,
				buttonLabelRef,
				emptyLabel,
				nodeToItem,
				clearSelectionRef,
			});

			clearSearch?.();
			if (isOpen.current) {
				onButtonClick();
			}
		}

		function onOptionClick(option) {
			const item = nodeToItem.get(option);
			onItemClick(item)();
		}

		function onItemClick(item) {
			return (event) => {
				event?.stopPropagation();
				onButtonClick();

				if (event.target.classList.contains(selectedCssClass)) {
					return;
				}

				selectOption({
					option: item,
					onChange,
					buttonLabelRef,
					emptyLabel,
					nodeToItem,
					clearSelectionRef,
				});

				clearSearch?.();
				itemToNode.get(item).classList.add(selectedCssClass);
			};
		}

		function onBlur() {
			if (!isOpen.current) {
				return;
			}
			onButtonClick();
		}

		function matchItems({ allItems, searchFn, key }) {
			return allItems
				.map((item) => {
					if (item.value) {
						return searchFn({ item, searchText: key })
							? item
							: undefined;
					}

					const matchingItems = matchItems({
						allItems: item.options,
						searchFn,
						key,
					});

					if (matchingItems.length === 0) {
						return undefined;
					}

					return {
						label: item.label,
						options: matchingItems,
					};
				})
				.filter((item) => item !== undefined);
		}

		function performSearch({ allItems, selectedItem, searchFn, key }) {
			if (!key) {
				setItems({ allItems, matchingItems: allItems, selectedItem });
				if (key !== null) {
					searchFieldRef.current?.childNodes[0].focus();
				}
				return;
			}

			const matchingItems = matchItems({ allItems, searchFn, key });

			setItems({ allItems, matchingItems, selectedItem });
			searchFieldRef.current?.childNodes[0].focus();

			if (!matchingItems.length) {
				const node = makeEmptyFilterNode();
				optionsRef.appendChild(node);
			}
		}

		const debouncedPerformSearch = debounce({
			fn: performSearch,
		});

		function setItems({ allItems, matchingItems, selectedItem }) {
			optionsRef.innerHTML = "";

			if (searchable && !searchFieldRef.current) {
				const node = makeSearchableField({
					allItems,
					selectedItem,
					searchFn:
						typeof searchable === "function"
							? searchable
							: defaultSearchFn,
					debouncedPerformSearch,
					buttonRef,
					onBlur,
					isOpen,
					onButtonClick,
				});

				searchFieldRef.current = node;
				selectContainerRef.current.insertBefore(node, optionsRef);
			}

			matchingItems.forEach((item) => {
				let node = itemToNode.get(item);

				node ??= makeNode({
					selectedItem,
					item,
					selectContainerRef,
					onItemClick,
					itemToNode,
					nodeToItem,
					disabled: item.value ? false : item.options.length === 0,
				});

				optionsRef.appendChild(node);
			});
		}

		/**
		 * We compute items and render the widget in parallel. When both are done, then we can fill up the list of
		 * options.
		 */
		Promise.all([itemsPromise, renderPromise]).then(([resolvedItems]) => {
			let selectedItem = findSelectedItem({
				items: resolvedItems,
				selected,
			});

			if (!selectedItem && !allowEmptySelection) {
				selectedItem = firstSelectableItem(items);
				onChange(selectedItem.value);
			}

			if (!selectedItem) {
				clearSelectionRef.classList.add("is-not-visible");
			}

			updateButtonLabelFromSelection({
				selectedItem,
				buttonLabelRef,
				buttonRef,
				emptyLabel,
				alwaysDisabled: disabled,
			});

			clearSearch = () => {
				performSearch({
					allItems: resolvedItems,
					selectedItem,
					key: null,
				});

				if (searchFieldRef.current) {
					searchFieldRef.current.childNodes[0].value = "";
				}
			};

			setItems({
				allItems: resolvedItems,
				matchingItems: resolvedItems,
				selectedItem,
			});
		});

		return {
			options: {},
			ref: (ref) => {
				buttonRef = ref;
				ref.setAttribute("disabled", true);
				ref.setAttribute("type", "button");
				if (name) ref.setAttribute("name", name);

				registerButtonKeyDown({
					buttonRef,
					isOpen,
					onButtonClick,
					searchFieldRef,
					searchable,
					selectContainerRef,
					nodeToItem,
					onOptionClick,
				});

				buttonRef.addEventListener("click", (event) => {
					event.preventDefault();

					if (selectContainerRef.current?.contains(event.target)) {
						return;
					}

					onButtonClick();
				});

				resolveRendered();
			},
			tag: "button",
			children: (html) => {
				html.render(
					stacker(
						{ spacing: "between" },
						text(
							{
								class: "button-text",
								ref: (ref) => (buttonLabelRef = ref),
							},
							html.div({ class: "css-loader" }),
						),
						stacker(
							{ size: small },
							allowEmptySelection &&
								button(
									{
										as: "span",
										class: "clear-selection",
										dataTest: "clear-selection",
										onClick: clearSelection,
										ref: (ref) => (clearSelectionRef = ref),
									},
									thinCircleTimesIcon,
								),
							thinAngleDownIcon({
								class: "select-icon",
							}),
						),
					),
				);

				let options;
				const list = html.span(
					{
						class: "select-list",
					},
					(html) => {
						options = html.ul({ class: "options" });
					},
				);

				optionsRef = options.element;
				selectContainerRef.current = list.element;
			},
		};
	},
);

/**
 * @typedef {GroupItem|OptionItem} Item
 *
 * Items entry.
 */

/**
 * @typedef {object} GroupItem
 *
 * Item grouping a list of items under a label.
 * @property {string} label - Label of the group
 * @property {Item[]} options - List of items to group
 * @property {bool} disabled - When true, the group and all its children are
 *   disabled
 */

/**
 * @typedef {object} OptionItem
 *
 * Item with a name and a value.
 * @property {string} label - Label of the item
 * @property {*} value - Value of the item. It must be different from all other
 *   items, and must not be null
 * @property {bool} disabled - When true, the item can not be selected
 * @property {bool} selected - When true, the item is selected by default.
 *   The `selected` property of the select element takes precedence over this.
 *   In case of multiple items with a `selected` property set to true, the
 *   first one wins
 */
